import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useElementSize } from "usehooks-ts";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdHelpOutline } from "react-icons/md";
import { RootState, dispatchAction } from "../../../state/store";
import { focusSecondaryFilter, focusLocationFilter } from "../../../state/features/analytics/actions";
import SelectVisual from "./selectVisual";
import {
  filterWidgetUrlsBySelectedTimePeriod,
  getSectionTabTitles,
  type ChildWidgetWithTrendPeriod,
} from "./utils";
import { FaMagnifyingGlassArrowRight } from "react-icons/fa6";
import { useAnalyticsGroupWidgetContext } from "../../../helpers/hooks/useAnalyticsGroupWidgetContext";
import WidgetLockedUnauthorized from "../../widgetLockedUnauthorized";
import AiAdvisorCta from "../../../features/ai/aiAdvisorCta";

const TrendsWidget = () => {
  const [chartContainerRef, chartDimensions] = useElementSize();
  const { filters } = useSelector((state: RootState) => state);
  const [selectedTab, setSelectedTab] = useState<ChildWidgetWithTrendPeriod | null>(null);
  const [widgetHasNoData, setWidgetHasNoData] = useState(true);
  const [filterLinkConfig, setFilterLinkConfig] = useState<{
    startTextFragment: string;
    endTextFragment: string;
    formattedText: string;
    filterKey: string;
  } | null>(null);

  const {
    widgetData,
    apiError,
    fetchData,
    isLoading,
    widgetMeta,
    setWidgetUrl,
    enableExtendedAReport,
    onExtendedAClick,
    isAiSuggestionEnabled,
    isAiSuggestionLoadError,
    isAiSuggestionLoading,
    getAiSuggestion,
    widgetInputRequired,
    overrideMessage,
    toolTipMessage,
  } = useAnalyticsGroupWidgetContext();

  const widgetTabsMeta = widgetMeta?.sub_widgets ?? [];

  // Tabs visible in current date range selection
  const visibleTabs: ChildWidgetWithTrendPeriod[] = useMemo(() => {
    if (!widgetTabsMeta.length) {
      return [];
    }
    return filterWidgetUrlsBySelectedTimePeriod([filters.dates[0], filters.dates[1]], widgetTabsMeta);
  }, [JSON.stringify(filters.dates), JSON.stringify(widgetTabsMeta)]);

  const activeTabStatus = useRef<ChildWidgetWithTrendPeriod | null>(null);

  // Parse filter link configuration from overrideMessage
  useEffect(() => {
    if (overrideMessage) {
      const regexMatch = overrideMessage.match(/\[(.*)\]\((.*)\)/);
      if (regexMatch) {
        const [searchString, formattedText, filterKey] = regexMatch;
        // Assuming that there will be only one substring, split the text
        const [startTextFragment, endTextFragment] = overrideMessage.split(searchString);
        setFilterLinkConfig({
          startTextFragment,
          endTextFragment,
          formattedText,
          filterKey,
        });
      }
    }
  }, [overrideMessage]);

  useEffect(() => {
    if (visibleTabs.length) {
      if (!activeTabStatus.current) {
        setWidgetUrl(visibleTabs[0].url);
        setSelectedTab(visibleTabs[0]);
      } else {
        setWidgetUrl(activeTabStatus.current?.url);
        setSelectedTab(activeTabStatus.current);
      }
    }
  }, [JSON.stringify(visibleTabs)]);

  const handleTabSelection = (data: ChildWidgetWithTrendPeriod) => {
    setSelectedTab(data);
    setWidgetUrl(data?.url);
  };

  const handleFilterLinkClick = () => {
    if (filterLinkConfig?.filterKey !== "location_ids") {
      dispatchAction(focusSecondaryFilter(filterLinkConfig?.filterKey));
    } else {
      dispatchAction(focusLocationFilter(filterLinkConfig?.filterKey));
    }
  };

  return (
    <Wrapper ref={chartContainerRef}>
      <>
        <div className="section-header">
          <>
            <div className="widget-title">
              {isLoading ? "" : widgetMeta.title ?? ""}
            </div>
            {enableExtendedAReport && !isLoading && !widgetHasNoData ? (
              <div
                title="More details"
                className="extended-report-option"
                onClick={() => onExtendedAClick()}
              >
                <FaMagnifyingGlassArrowRight fontSize={"18px"} />
              </div>
            ) : null}
            {isAiSuggestionEnabled && !isLoading && !widgetHasNoData ? (
              <AiAdvisorCta
                showError={isAiSuggestionLoadError}
                showLoadingIndicator={isAiSuggestionLoading}
                onClick={() => getAiSuggestion()}
              />
            ) : null}
            <div className="tabs-section">
              {!widgetHasNoData &&
                visibleTabs.map((w, i) =>
                  isLoading ? null : (
                    <div
                      key={i}
                      className={`tab ${
                        selectedTab && selectedTab.url === w.url
                          ? `selected`
                          : ``
                      }`}
                      onClick={() => handleTabSelection(w)}
                    >
                      {getSectionTabTitles(w.url)}
                    </div>
                  )
                )}
            </div>
          </>
        </div>
        <div
          className="section-data"
          style={
            chartDimensions ? { height: chartDimensions.height - 120 } : {}
          }
        >
          {widgetInputRequired && overrideMessage && !isLoading ? (
            <div className="input-required-content">
              {filterLinkConfig ? (
                <div className="override-message">
                  {filterLinkConfig.startTextFragment}
                  <span className="filter-link" onClick={handleFilterLinkClick}>
                    {filterLinkConfig.formattedText}
                  </span>
                  {filterLinkConfig.endTextFragment}{" "}
                  {toolTipMessage && (
                    <OverlayTrigger
                      trigger={["hover", "focus"]}
                      overlay={
                        <Tooltip id="override-message-tooltip">
                          {toolTipMessage}
                        </Tooltip>
                      }
                    >
                      <span>
                        <MdHelpOutline
                          style={{
                            fontSize: "19px",
                            position: "relative",
                            bottom: "2px",
                          }}
                        />
                      </span>
                    </OverlayTrigger>
                  )}
                </div>
              ) : (
                <div className="override-message">{overrideMessage}</div>
              )}
            </div>
          ) : selectedTab ? (
            <SelectVisual
              width={chartDimensions.width - 50}
              height={chartDimensions.height - 120}
              trendPeriod={selectedTab?.url}
              selectedTab={selectedTab}
              error={apiError}
              isLoading={isLoading}
              refetchData={fetchData}
              setWidgetHasNoData={setWidgetHasNoData}
              widgetData={widgetData}
            />
          ) : (
            <WidgetLockedUnauthorized type="a_level" />
          )}
        </div>
      </>
    </Wrapper>
  );
};

export default TrendsWidget;

const Wrapper = styled.div`
  border-radius: 20px;
  background-color: ${({ theme }) => theme.widget.background};
  filter: drop-shadow(5px 5px 10px rgba(0, 0, 0, 0.05));
  width: 100%;
  height: 100%;
  padding: 25px;
  .section-header {
    display: flex;
    .widget-title {
      flex: 1;
      text-decoration: underline;
    }
    .extended-report-option {
      box-sizing: border-box;
      height: 35px;
      width: 35px;
      cursor: pointer;
      position: relative;
      top: 7px;
      color: ${({ theme }) => theme.button_plain.text};
      border: 1.5px solid ${({ theme }) => theme.button_plain.border};
      &:hover {
        color: ${({ theme }) => theme.button_plain.hover_text};
        background: ${({ theme }) => theme.button_plain.hover};
      }
      border-radius: 8px;
      display: flex;
      justify-content: center;
      padding: 2px 8px;
      padding: 0px;
      align-items: center;
    }
    .ai-advisor-cta {
      position: relative;
      top: 7px;
    }
    .tabs-section {
      display: flex;
      justify-content: flex-end;
      flex-wrap: wrap;
      .tab {
        cursor: pointer;
        min-width: 80px;
        padding: 10px;
        margin: 5px;
        white-space: nowrap;
        font-size: 14px;
        font-weight: 500;
        line-height: 18px;
        text-align: center;
        background: ${({ theme }) => theme.body};
        border-radius: 20px;
        color: ${({ theme }) => theme.text};
        &.selected {
          background: ${({ theme }) => theme.text};
          color: ${({ theme }) => theme.body};
        }
      }
    }
  }

  @media (max-width: 995px) {
    padding: 20px;
    .section-header {
      flex-wrap: wrap;
      row-gap: 8px;
      .tabs-section .tab {
        min-width: 0;
        padding: 8px 12px;
        margin: 4px;
        font-size: 13px;
      }
    }
  }

  @media (max-width: 767px) {
    padding: 16px;
    border-radius: 16px;
    .section-header {
      flex-wrap: wrap;
      row-gap: 10px;
      align-items: center;
      .widget-title {
        flex: 1 1 auto;
        min-width: 0;
      }
      .tabs-section {
        flex: 0 0 100%;
        justify-content: flex-start;
        gap: 4px;
        margin: 0;
        .tab {
          flex: 0 0 auto;
          min-width: 0;
          padding: 6px 10px;
          margin: 0;
          font-size: 12px;
          line-height: 16px;
          border-radius: 16px;
        }
      }
    }
  }

  .input-required-content {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 20px;
    text-align: center;
    
    .override-message {
      font-size: 16px;
      color: ${({ theme }) => theme.text};
      line-height: 1.5;
      
      .filter-link {
        color: ${({ theme }) => theme.primary};
        cursor: pointer;
        text-decoration: underline;
        font-weight: 500;
        
        &:hover {
          color: ${({ theme }) => theme.primaryHover || theme.primary};
          opacity: 0.8;
        }
      }
    }
  }
`;
